import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PortableText } from '@portabletext/react';
import { client, urlFor } from '../client';
import { format } from 'date-fns';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { Helmet } from 'react-helmet-async';

export default function SinglePost() {
  const { slug } = useParams();
  const [postData, setPostData] = useState(null);

  useEffect(() => {
    // Fetch the post matching the slug
    client
      .fetch(
        `*[slug.current == $slug]{
          title,
          slug,
          mainImage,
          publishedAt,
          body,
          "name": author->name,
          "authorImage": author->image
       }`,
        { slug }
      )
      .then((data) => setPostData(data[0]))
      .catch(console.error);
  }, [slug]);

  if (!postData) return (
    <div className="section" style={{minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center"}}>
      <h3>Loading post...</h3>
    </div>
  );

  return (
    <main>
      <Helmet>
        <title>{postData.title} | Kunal Das</title>
        <meta name="description" content={`Read "${postData.title}" by ${postData.name || 'Kunal Das'}.`} />
        {postData.mainImage && (
          <meta property="og:image" content={urlFor(postData.mainImage).width(1200).url()} />
        )}
        <meta property="og:title" content={postData.title} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://kunaldas.tech/blog/${slug}`} />
        <link rel="canonical" href={`https://kunaldas.tech/blog/${slug}`} />
      </Helmet>
      <section className="section pb-5 pt-5 mt-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="text-center mb-5">
                {postData.publishedAt && (
                  <p className="text-muted mb-2">
                    {format(new Date(postData.publishedAt), 'MMMM dd, yyyy')}
                  </p>
                )}
                <h1 className="fw-bold mb-4">{postData.title}</h1>
                {postData.name && (
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    {postData.authorImage && (
                      <img
                        src={urlFor(postData.authorImage).width(50).url()}
                        alt={postData.name}
                        className="rounded-circle"
                        style={{ width: "40px", height: "40px", objectFit: "cover" }}
                      />
                    )}
                    <span className="text-muted">By {postData.name}</span>
                  </div>
                )}
              </div>

              {postData.mainImage && (
                <div className="mb-5 rounded overflow-hidden shadow-lg">
                  <img
                    src={urlFor(postData.mainImage).width(1200).url()}
                    alt={postData.title}
                    className="img-fluid w-100"
                    style={{ maxHeight: "500px", objectFit: "cover" }}
                  />
                </div>
              )}

              <div className="blog-content mb-5" style={{ fontSize: "1.1rem", lineHeight: "1.8" }}>
                <PortableText
                  value={postData.body}
                  components={{
                    types: {
                      image: ({ value }) => {
                        if (!value?.asset?._ref) {
                          return null;
                        }
                        return (
                          <figure style={{ margin: '2rem 0', textAlign: 'center' }}>
                            <img
                              alt={value.alt || ' '}
                              loading="lazy"
                              src={urlFor(value).width(800).url()}
                              style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px' }}
                            />
                            {value.alt && (
                              <figcaption style={{ 
                                marginTop: '0.8rem', 
                                fontSize: '0.95rem', 
                                color: '#888',
                                fontStyle: 'italic',
                                fontWeight: '500'
                              }}>
                                {value.alt}
                              </figcaption>
                            )}
                          </figure>
                        );
                      },
                      code: ({ value }) => {
                        if (!value || !value.code) { return null; }
                        return (
                          <div className="my-4 shadow-sm rounded overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                            <div className="px-3 py-2 d-flex justify-content-between align-items-center" style={{ backgroundColor: '#1e1e1e', borderBottom: '1px solid #333' }}>
                              <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>
                                {value.language || 'text'}
                              </small>
                            </div>
                            <SyntaxHighlighter
                              language={value.language || 'text'}
                              style={vscDarkPlus}
                              customStyle={{ margin: 0, padding: '1.5rem', borderRadius: '0 0 0.5rem 0.5rem', fontSize: '0.9rem' }}
                            >
                              {value.code}
                            </SyntaxHighlighter>
                          </div>
                        );
                      },
                    },
                    marks: {
                      link: ({children, value}) => {
                        const rel = !value.href.startsWith('/') ? 'noreferrer noopener' : undefined;
                        return (
                          <a href={value.href} rel={rel} style={{color: "var(--px-theme)", textDecoration: "none"}}>
                            {children}
                          </a>
                        );
                      },
                      alignLeft: ({children}) => <span style={{display: 'block', textAlign: 'left'}}>{children}</span>,
                      alignCenter: ({children}) => <span style={{display: 'block', textAlign: 'center'}}>{children}</span>,
                      alignRight: ({children}) => <span style={{display: 'block', textAlign: 'right'}}>{children}</span>,
                      alignJustify: ({children}) => <span style={{display: 'block', textAlign: 'justify'}}>{children}</span>,
                    },
                  }}
                />
              </div>
              
              <div className="text-center mt-5 mb-5 border-top border-secondary pt-4">
                <Link to="/" className="btn btn-primary">
                  &larr; Back to Portfolio
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
